import type { MpidRequest, MpidResult, Product } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/library'
import { sleep } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/functions'
import type { Backend } from '@/lib/backend-cors/backend'
import type { BankIDDataResponse, CreateImageCollectionResponse, CreateImageResponse, ElhubConsentAlreadySignedResponse, ElhubConsentData, ElhubConsentErrorResponse, ElhubConsentResponse, ElhubConsentSessionResponse, OrderResponse, UIResponse } from '@/lib/backend-cors/types'
import { useAxios } from '@/lib/backend-cors/axios'
import { Origin, useGenericStore } from '@/stores/generic'
import { storeFile } from '@/lib/backend-cors/vapor'
import type { ProtocolSubmission } from '@/stores/protocol.ts'
import type { AxiosResponse } from 'axios'
import { ElhubConsentErrorCode } from '@/lib/backend-cors/types'
import config from '@/config'
import type { Backup } from '@/stores/backup.ts'

const PYLON_V1 = 'pylon-v1'
const PYLON_V2 = 'pylon-v2'

export const usePylon = (): Backend => {
	const v1 = useAxios({
		name: PYLON_V1,
		baseURL: import.meta.env.ACQUISIT_PYLON_V1_URL
	})
	
	const v2 = useAxios({
		name: PYLON_V2,
		baseURL: import.meta.env.ACQUISIT_PYLON_V2_URL
	})
	
	const isDevOrStaging = import.meta.env.ACQUISIT_MODE == 'development' ||
	                       import.meta.env.ACQUISIT_MODE == 'staging'
	
	// Assume v2 by default, changes to v1 if outside says so
	let axios = v2
	
	// Helper function to check if we should use BankID dummy in development
	const shouldUseBankIDDummy = (): boolean => {
		if (!isDevOrStaging) {
			return false
		}
		
		const currentParams = new URLSearchParams(window.location.search)
		
		if (config.backend.bankid_dummy) {
			return currentParams.get('bankid') !== '1'
		} else {
			return currentParams.get('bankid') === '1337'
		}
	}
	
	const shouldUseElhubDummy = (): boolean => {
		if (!isDevOrStaging) {
			return false
		}
		
		const currentParams = new URLSearchParams(window.location.search)
		
		if (config.backend.elhub_dummy) {
			return currentParams.get('elhub') !== '1'
		} else {
			return currentParams.get('elhub') === '1337'
		}
	}
	
	const getProtocolOrigin = async (...args: string[]): Promise<Origin> => {
		if (args.length === 1) {
			// token
			try {
				await v2.head('/order/check/' + args[0], {}, false)
				
				axios = v2
				return Origin.UI_BUILDER
			} catch (e: any) {
				axios = v1
				return Origin.CLASSIC
			}
		} else {
			// ticketID, password
			const [ ticketID, password ] = args
			
			try {
				await v2.head('/order/check/' + ticketID + '/' + password, {}, false)
				
				axios = v2
				return Origin.UI_BUILDER
			} catch (e: any) {
				axios = v1
				return Origin.CLASSIC
			}
		}
	}
	
	return {
		get label(): string {
			return axios.name
		},
		
		get baseURL(): string {
			return axios.baseURL
		},
		
		retry: axios.retry,
		
		// Protocols
		getProtocolOrigin,
		
		async getOrderByTicket(ticketID: string, password: string): Promise<OrderResponse> {
			const response = await axios.request<OrderResponse>('get', '/order/' + ticketID + '/' + password, {}, {}, {
				'X-Navigator': navigator.userAgent,
				'X-Acquisit-Host': window.location.host
			}, false)
			
			return response.data
		},
		
		async getOrderByToken(token: string): Promise<OrderResponse> {
			const response = await axios.request<OrderResponse>('get', '/order/' + token + '?ts=' + (+new Date()), {}, {}, {
				'X-Navigator': navigator.userAgent,
				'X-Acquisit-Host': window.location.host
			}, false)
			
			return response.data
		},
		
		async getUI(token: string): Promise<UIResponse> {
			const response = await axios.request<UIResponse>('get', '/order/' + token + '/ui?ts=' + (+new Date()), {}, {}, {
				'X-Navigator': navigator.userAgent,
				'X-Acquisit-Host': window.location.host
			}, false)
			
			return response.data
		},
		
		async createConsent(token: string, submission: ProtocolSubmission, returnURL: string): Promise<ElhubConsentResponse> {
			if (shouldUseElhubDummy()) {
				const currentParams = new URLSearchParams(window.location.search)
				
				if (currentParams.get('elhubType') == 'already_signed') {
					return {
						type: 'already_signed',
						message: 'DEBUG: This consent has alrady been signed.',
					} as ElhubConsentAlreadySignedResponse
				}
				
				const url = new URL(window.location.href)
				url.pathname = '/elhub-dummy.html'
				url.searchParams.set('protocolUrl', returnURL)

				await sleep(2000)
				
				return {
					url: String(url),
					type: 'signing'
				} as ElhubConsentSessionResponse
			}
			
			// Append token so Pylon can verify later on
			submission.token = token
			
			const cloudFile = await storeFile(JSON.stringify(submission), {
				contentType: 'application/json'
			})
			
			return (await axios.post<ElhubConsentResponse>('/consent/' + token, {
				protocolUrl: returnURL
			}, cloudFile, false)).data
		},
		
		async getConsentData(token: string, sessionID: string): Promise<ElhubConsentData> {
			if (shouldUseElhubDummy()) {
				const url = new URL(window.location.href)

				await sleep(2000)
				
				if (url.searchParams.get('simulated-response') === 'error-on-data') {
					throw {
						response: {
							data: {
								error: 'A simulated error occured.',
								error_code: ElhubConsentErrorCode.ELHUB_PAYLOAD_INVALID
							} satisfies ElhubConsentErrorResponse
						}
					}
				} else {
					return {
						message: 'Avtalekontroll fra Elhub er signert og godkjent, du kan gå videre til neste side.',
						protocol_signatures: [
							{
								id: 1,
								mpid: '12345678901234',
								elhub_consent_id: 'consent-abc',
								signicat_status: 'signed',
								protocol_file_id: 456
							},
							{
								id: 2,
								mpid: '98765432198765',
								elhub_consent_id: 'consent-def',
								signicat_status: 'signed',
								protocol_file_id: 789
							}
						]
					} satisfies ElhubConsentData
				}
			}
			
			return (await axios.get<ElhubConsentData>('/consent/' + token + '/session', {
				signicatSessionId: sessionID
			})).data
		},
		
		async submitProtocol(token: string, payload: Record<string, any>): Promise<AxiosResponse> {
			// Check which type of template we have. For UI Builder we need to upload to Vapor first, then pass its data to Pylon
			// For classic we post straight to pylon
			if (useGenericStore().is_origin_ui_builder) {
				// Append token so Pylon can verify later on
				payload.token = token
				
				const cloudFile = await storeFile(JSON.stringify(payload), {
					contentType: 'application/json'
				})
				
				return await v2.post('/order/' + token + '/validate', {}, cloudFile, false)
			} else {
				return await v1.put('/order/' + token + '/protocol', {}, payload, false)
			}
		},
		
		// Backups
		
		async getLatestBackup(token: string): Promise<Record<string, any> | null> {
			const response = await axios.request('get', '/backups/' + token + '/latest?ts=' + (+new Date()), {}, {}, {}, false)
			
			return response.data
		},
		
		async storeBackup(token: string, backup: Backup): Promise<void> {
			if (useGenericStore().is_origin_ui_builder) {
				const cloudFile = await storeFile(JSON.stringify(backup), {
					contentType: 'application/json'
				})
				
				await v2.put('/backups/' + token, {}, cloudFile, false)
			} else {
				await v1.put('/backups/' + token, {}, backup)
			}
		},
		
		async deleteBackup(token: string): Promise<void> {
			await axios.delete('/backups/' + token)
		},
		
		// Images
		
		async createImageCollection(
			token: string,
			title: string | null,
			semantic: string | null,
			metadata: Record<string, any> | null
		): Promise<CreateImageCollectionResponse> {
			return (await axios.put<CreateImageCollectionResponse>('/order/' + token + '/collection', {}, {
				title,
				semantic,
				metadata
			})).data
		},
		
		async updateImageCollection(
			token: string,
			imageCollectionID: string | number,
			title: string | null,
			metadata: Record<string, any> | null
		): Promise<void> {
			await axios.patch('/order/' + token + '/collections/' + imageCollectionID, {}, {
				title,
				metadata
			})
		},
		
		async uploadImage(
			token: string,
			imageCollectionID: string | number,
			image: string,
			title: string | null
		): Promise<CreateImageResponse> {
			if (useGenericStore().is_origin_ui_builder) {
				const cloudFile = await storeFile(image, {
					contentType: 'application/octet-stream'
				})
				
				return (await v2.put<CreateImageResponse>('/order/' + token + '/collections/' + imageCollectionID + '/image', {}, {
					...cloudFile,
					title
				})).data
			} else {
				return (await v1.put('/order/' + token + '/collections/' + imageCollectionID + '/image', {}, {
					image,
					title
				})).data
			}
		},
		
		async deleteImageCollection(token: string, imageCollectionID: string | number): Promise<void> {
			// Only v1 supports this at the moment
			await v1.delete('/order/' + token + '/collections/' + imageCollectionID)
		},
		
		async deleteImage(token: string, imageCollectionID: string | number, imageID: string | number): Promise<void> {
			// Only v1 supports this at the moment
			await v1.delete('/order/' + token + '/collections/' + imageCollectionID + '/image/' + imageID)
		},
		
		// BankID
		
		getBankIDURL(token: string, personID: string, personNumber: string, returnURL: string): string {
			if (shouldUseBankIDDummy()) {
				const url = new URL(window.location.href)
				url.pathname = '/bankid-dummy.html'
				url.searchParams.set('protocolUrl', returnURL)
				url.searchParams.set('personId', personID)
				url.searchParams.set('personNumber', personNumber)
				
				return url.href
			}
			
			let bankID = axios.makeURL('/signicat/' + token + '/' + personID, {
				protocolUrl: returnURL,
				personNumber: personNumber
			})
			
			if (!bankID.startsWith('http')) { // Make it relative to the current URL if it's not absolute already
				bankID = window.location.protocol + '//' + window.location.host + bankID
			}
			
			const url = new URL(bankID)
			
			return url.href
		},
		
		async getBankIDData(token: string, personID: string, sessionID: string): Promise<BankIDDataResponse> {
			if (shouldUseBankIDDummy()) {
				return {
					common_name: 'Dummy Person Debug',
					person_number: sessionID,
					timestamp: Math.floor((+new Date()) / 1000)
				}
			}
			
			const response = await v1.post('/signicat/' + token + '/' + personID + '/' + sessionID, {}, {})
			
			return response.data
		},
		
		// Products
		
		async getProductTerms(token: string, product: Product): Promise<string | null> {
			const productID = product.product_id ?? product.uid
			
			const url = product.product_type == 'electricity' ?
			            '/products/' + productID + '/terms/' + token :
			            '/all-products/' + productID + '/terms/' + token
			
			const response = await axios.request('get', url + '?ts=' + (+new Date()), {}, {}, {
				'Content-Type': 'text/plain'
			}, false)
			
			return response.data
		},
		
		// Målepunkt ID
		
		async fetchMpid(token: string, request: MpidRequest): Promise<MpidResult[]> {
			if (shouldUseElhubDummy()) {
				return [
					{
						mpid: '707057500012345678',
						meter_number: '123456789123',
						street_name: 'Demoveien',
						street_number: '5',
						postcode: '0301',
						city: 'OSLO',
						full_address: 'Demoveien 5'
					}
				] satisfies MpidResult[]
			}
			
			const response = await v2.post('/order/' + token + '/elhub/search', {}, {
				zipcode: request.postcode,
				mpid: request.mpid,
				meter_number: request.meter_number
			}, false)
			
			return response.data.map((r: any) => {
				r.postcode = r.zipcode
				delete r.zipcode
				
				return r
			}) as MpidResult[]
		},
		
		// Reviews
		
		async submitReview(token: string, score: number, comment: string): Promise<void> {
			await v1.put('/order/' + token + '/review', {}, {
				score,
				comment
			})
		},
		
		parseError(error: any): any {
			return axios.parseError(error)
		},
		
		onError(error: any): any {
			return axios.onError(error)
		},
	}
}